import { App as AntApp, ConfigProvider, Empty } from "antd";
import arEG from "antd/locale/ar_EG";
import enGB from "antd/locale/en_GB";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en-gb";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { dirOf, type Language } from "@ap/i18n";
import { buildTheme, cssVars, type ColorMode } from "./theme";

/** "system" follows `prefers-color-scheme`. */
export type ColorPreference = ColorMode | "system";

export const COLOR_MODE_KEY = "ap.theme";

export function readColorPreference(store?: Storage): ColorPreference {
  try {
    const v = (store ?? localStorage).getItem(COLOR_MODE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

export function writeColorPreference(p: ColorPreference, store?: Storage): void {
  try {
    const s = store ?? localStorage;
    if (p === "system") s.removeItem(COLOR_MODE_KEY);
    else s.setItem(COLOR_MODE_KEY, p);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

function useMedia(query: string): boolean {
  const get = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return matches;
}

export interface ColorModeValue {
  preference: ColorPreference;
  /** The mode in effect. */
  mode: ColorMode;
  setPreference: (p: ColorPreference) => void;
  /** Switch to the other explicit mode. */
  toggle: () => void;
  reducedMotion: boolean;
}

const ColorModeContext = createContext<ColorModeValue>({
  preference: "system",
  mode: "light",
  setPreference: () => {},
  toggle: () => {},
  reducedMotion: false,
});

export function useColorMode(): ColorModeValue {
  return useContext(ColorModeContext);
}

/**
 * antd ConfigProvider with direction and locale switched together with the language, and the
 * design-system theme for the current colour mode (ADR-0055).
 */
export function AppProviders({ lang, children }: { lang: Language; children: ReactNode }) {
  const dir = dirOf(lang);
  const [preference, setPref] = useState<ColorPreference>(() => readColorPreference());
  const prefersDark = useMedia("(prefers-color-scheme: dark)");
  const reducedMotion = useMedia("(prefers-reduced-motion: reduce)");
  const mode: ColorMode = preference === "system" ? (prefersDark ? "dark" : "light") : preference;

  useEffect(() => {
    dayjs.locale(lang === "ar" ? "ar" : "en-gb");
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = dir;
    }
  }, [lang, dir]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    for (const [k, v] of Object.entries(cssVars(mode))) root.style.setProperty(k, v);
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
  }, [mode]);

  const setPreference = useCallback((p: ColorPreference) => {
    writeColorPreference(p);
    setPref(p);
  }, []);
  const toggle = useCallback(() => setPreference(mode === "dark" ? "light" : "dark"), [mode, setPreference]);
  const value = useMemo(() => ({ preference, mode, setPreference, toggle, reducedMotion }), [preference, mode, setPreference, toggle, reducedMotion]);
  const theme = useMemo(() => buildTheme(mode, reducedMotion), [mode, reducedMotion]);
  // Every empty table, list and select speaks the user's language (antd's own text is English-only
  // in places); screens still pass their own emptyText with a next step where they have one.
  const { t } = useTranslation("common");
  const renderEmpty = useCallback(() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />, [t]);

  return (
    <ColorModeContext.Provider value={value}>
      <ConfigProvider direction={dir} locale={lang === "ar" ? arEG : enGB} theme={theme} renderEmpty={renderEmpty}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
}
